/**
 * data-layer.js — CakeMiami Behavioral Event Tracking
 * Pushes link_call, link_sms, link_whatsapp, link_directions, link_form, click_faq
 * to window.dataLayer via event delegation.
 */
(function () {
  'use strict';

  function isDirectionsLink(href) {
    return /google\.\w+\/maps|maps\.google\.\w+|maps\.app\.goo\.gl|goo\.gl\/maps|maps\.apple\.com|waze\.com/i.test(href);
  }

  function isFormLink(href, pathname) {
    if (/^#(ctm-form|contact-form)$/.test(href)) return true;
    if (pathname === '/pages/contact' || pathname.indexOf('/pages/contact/') === 0) return true;
    return false;
  }

  function getSmsTarget(href) {
    return href.replace(/^sms:/i, '').split(/[?;&]/)[0];
  }

  function setup() {
    window.dataLayer = window.dataLayer || [];
    removeLegacyUgcProductSections();

    // Link click delegation
    document.addEventListener('click', function (e) {
      var target = e.target;
      var anchor = target.closest ? target.closest('a') : null;
      if (!anchor) return;

      var href = anchor.getAttribute('href') || '';
      var linkText = (anchor.textContent || '').replace(/\s+/g, ' ').trim() || anchor.getAttribute('aria-label') || '';
      var ctaEl = anchor.closest('[data-cta-location]');
      var linkLocation = ctaEl ? ctaEl.getAttribute('data-cta-location') : null;

      var payload = {
        link_text: linkText,
        page_path: window.location.pathname
      };
      if (linkLocation) payload.link_location = linkLocation;

      // 1. tel: -> link_call
      if (href.indexOf('tel:') === 0) {
        payload.event = 'link_call';
        window.dataLayer.push(payload);
        return;
      }

      // 2. sms: -> link_sms
      if (href.toLowerCase().indexOf('sms:') === 0) {
        payload.event = 'link_sms';
        payload.sms_target = getSmsTarget(href);
        window.dataLayer.push(payload);
        return;
      }

      // 3. wa.me -> link_whatsapp
      if (href.indexOf('wa.me') !== -1) {
        payload.event = 'link_whatsapp';
        window.dataLayer.push(payload);
        return;
      }

      // 4. directions -> link_directions
      if (isDirectionsLink(href)) {
        payload.event = 'link_directions';
        window.dataLayer.push(payload);
        return;
      }

      // 5. form -> link_form
      var anchorPathname = anchor.pathname || '/';
      if (isFormLink(href, anchorPathname)) {
        payload.event = 'link_form';
        window.dataLayer.push(payload);
        return;
      }

      // 6. All other links -> no event
    });

    // FAQ Pattern A: button-based (.faq-question)
    document.addEventListener('click', function (e) {
      var target = e.target;
      var button = target.closest ? target.closest('.faq-question') : null;
      if (!button) return;

      var question = (button.textContent || '').replace(/\s+/g, ' ').trim();
      if (question) {
        window.dataLayer.push({
          event: 'click_faq',
          faq_question: question,
          page_path: window.location.pathname
        });
      }
    });

    // FAQ Pattern B: <details> toggle (capture phase), scoped to data-track-faq containers
    document.addEventListener('toggle', function (e) {
      var details = e.target;
      if (details.tagName !== 'DETAILS' || !details.open) return;
      if (!details.closest('[data-track-faq]')) return;

      var h3 = details.querySelector('h3');
      var summary = details.querySelector('summary');
      var question = (h3 ? h3.textContent : summary ? summary.textContent : '');
      question = question.replace(/\s+/g, ' ').trim();

      if (question) {
        window.dataLayer.push({
          event: 'click_faq',
          faq_question: question,
          page_path: window.location.pathname
        });
      }
    }, true);

    repairStaleProductPageCache();
  }

  function wireProductTabs(container) {
    var tabContainer = container.querySelector('.product-info-tabs__tabs');
    if (!tabContainer) return;

    var buttons = tabContainer.querySelectorAll('.product-info-tabs__nav-item');
    var panels = tabContainer.querySelectorAll('.product-info-tabs__panel');

    buttons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();

        var tabIndex = button.getAttribute('data-tab-index');
        buttons.forEach(function (btn) {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        });
        panels.forEach(function (panel) {
          panel.classList.remove('is-active');
          panel.style.display = 'none';
        });

        button.classList.add('is-active');
        button.setAttribute('aria-selected', 'true');

        var panelId = button.getAttribute('aria-controls');
        var activePanel = panelId ? document.getElementById(panelId) : null;
        if (activePanel) {
          activePanel.classList.add('is-active');
          activePanel.style.display = 'block';
          initProductFaqAccordion(activePanel);
        }
      });
    });

    initProductFaqAccordion(container);
  }

  function initProductFaqAccordion(container) {
    var faqItems = container.querySelectorAll('.faq-item');
    faqItems.forEach(function (item) {
      var question = item.querySelector('.faq-question');
      if (!question || question.getAttribute('data-cakemiami-faq-ready') === 'true') return;

      question.setAttribute('data-cakemiami-faq-ready', 'true');

      if (!question.querySelector('.faq-q-icon')) {
        var qIcon = document.createElement('span');
        qIcon.className = 'faq-q-icon';
        qIcon.innerHTML = 'Q&#58;&nbsp;';
        qIcon.style.whiteSpace = 'nowrap';
        question.insertBefore(qIcon, question.firstChild);
      }

      if (!question.querySelector('.faq-toggle-icon')) {
        var toggleIcon = document.createElement('span');
        toggleIcon.className = 'faq-toggle-icon';
        toggleIcon.textContent = '+';
        question.appendChild(toggleIcon);
      }

      question.addEventListener('click', function () {
        item.classList.toggle('active');

        var toggleIcon = question.querySelector('.faq-toggle-icon');
        if (toggleIcon) {
          toggleIcon.textContent = item.classList.contains('active') ? 'x' : '+';
        }
      });
    });
  }

  function parseSectionHtml(html) {
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html || '';
    return wrapper.firstElementChild;
  }

  function isUgcProductPage() {
    return !!document.querySelector('[data-pug-gallery], [data-pug-ugc], [data-pug-reviews], .pug-addons-host');
  }

  function removeLegacyUgcProductSections() {
    if (!/\/products\//.test(window.location.pathname) || !isUgcProductPage()) return;

    var legacyFeatured = document.querySelector('[id$="__featured_collection_Ff6Yc6"]');
    if (legacyFeatured) legacyFeatured.remove();
  }

  function repairStaleProductPageCache() {
    if (!/\/products\//.test(window.location.pathname)) return;

    window.setTimeout(function () {
      var tabSection = document.querySelector('.section-product-info-tabs');
      var tabs = tabSection ? tabSection.querySelector('.product-info-tabs') : null;
      if (!tabSection || !tabs) return;

      var tabButtons = tabSection.querySelectorAll('.product-info-tabs__nav-item');
      var tabRect = tabs.getBoundingClientRect();
      var isBroken = tabRect.height === 0 || tabButtons.length === 0;
      if (!isBroken) return;

      var tabSectionId = (tabSection.id || '').replace(/^shopify-section-/, '');
      if (!tabSectionId || tabSectionId.indexOf('__product_info_tabs') === -1) return;

      var isUgcPdp = isUgcProductPage();
      var featuredSectionId = tabSectionId.replace('__product_info_tabs', '__featured_collection_Ff6Yc6');
      var reviewSectionIds = [
        tabSectionId.replace('__product_info_tabs', '__product_reviews'),
        tabSectionId.replace('__product_info_tabs', '__product_reviews_ugc')
      ];
      var sectionIds = isUgcPdp ? [tabSectionId] : [tabSectionId, featuredSectionId].concat(reviewSectionIds);
      var sectionsUrl = window.location.pathname + '?sections=' + sectionIds.map(encodeURIComponent).join(',');

      fetch(sectionsUrl, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Section render failed');
          return response.json();
        })
        .then(function (sections) {
          var freshTabs = parseSectionHtml(sections[tabSectionId]);
          if (!freshTabs) return;

          tabSection.replaceWith(freshTabs);
          wireProductTabs(freshTabs);
          removeLegacyUgcProductSections();

          if (isUgcPdp) return;

          var freshFeatured = parseSectionHtml(sections[featuredSectionId]);
          var freshReviews = null;
          var insertAfter = freshTabs;
          if (freshFeatured && !document.getElementById('shopify-section-' + featuredSectionId)) {
            insertAfter.insertAdjacentElement('afterend', freshFeatured);
            insertAfter = freshFeatured;
          }

          reviewSectionIds.some(function (reviewsSectionId) {
            freshReviews = parseSectionHtml(sections[reviewsSectionId]);
            if (freshReviews && !document.getElementById('shopify-section-' + reviewsSectionId)) {
              insertAfter.insertAdjacentElement('afterend', freshReviews);
              return true;
            }
            return false;
          });
        })
        .catch(function () {
          window.dataLayer.push({
            event: 'product_tabs_repair_failed',
            page_path: window.location.pathname
          });
        });
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
